import { IS_PROD, IS_STAGING } from '@diamond/shared/environments';

import { buyerCaptureException } from './sentry-capture-exception';

/** Anonymous custom ID used after logout (matches Firebase `user_id` default). */
export const ANONYMOUS_CLARITY_USER_ID = '-';

export type ClarityContextTags = {
  shipto_no: string;
  shipto_name: string;
  cg3_code: string;
  cg3_name: string;
  page: string;
};

type ClarityApi = {
  init: (projectId: string) => void;
  identify: (customId: string) => void;
  setTag: (key: string, value: string) => void;
  consentV2: () => void;
};

let initialized = false;
let clarityApi: ClarityApi | null | undefined;

const canRun = (): boolean =>
  typeof window !== 'undefined' &&
  (IS_PROD || IS_STAGING) &&
  Boolean(process.env['NX_CLARITY_PROJECT_ID']);

const reportClarityFailure = (operation: string, error: unknown): void => {
  console.warn(`[clarity] ${operation} failed`, error);

  const message =
    error instanceof Error ? error.message : String(error ?? 'unknown error');

  buyerCaptureException({ message }, (scope) => {
    scope.setTag('feature', 'clarity');
    scope.setTag('clarity_operation', operation);
    scope.setLevel('warning');
  });
};

/**
 * Lazily resolves `@microsoft/clarity` only in the browser. The package is
 * ESM-only; a top-level import would break Node (API) and Jest (CJS).
 * Browser bundlers (Next/webpack) rewrite this require and ship the client SDK.
 */
const getClarity = (): ClarityApi | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  if (clarityApi !== undefined) {
    return clarityApi;
  }

  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const mod = require('@microsoft/clarity') as {
      default?: ClarityApi;
    } & ClarityApi;
    const clarity = mod.default ?? mod;

    if (
      typeof clarity.init !== 'function' ||
      typeof clarity.identify !== 'function' ||
      typeof clarity.setTag !== 'function' ||
      typeof clarity.consentV2 !== 'function'
    ) {
      reportClarityFailure(
        'load',
        new Error('Clarity module missing expected API')
      );
      clarityApi = null;
      return null;
    }

    clarityApi = clarity;
    return clarityApi;
  } catch (error) {
    reportClarityFailure('load', error);
    clarityApi = null;
    return null;
  }
};

/**
 * Initializes Microsoft Clarity once per app boot and grants Clarity storage
 * consent by default (no CMP yet). No-op outside production/staging builds or
 * when NX_CLARITY_PROJECT_ID is unset.
 */
export const initClarity = (): void => {
  if (!canRun() || initialized) return;

  const projectId = process.env['NX_CLARITY_PROJECT_ID'];
  if (!projectId) return;

  const clarity = getClarity();
  if (!clarity) return;

  try {
    clarity.init(projectId);
    clarity.consentV2();
    initialized = true;
  } catch (error) {
    reportClarityFailure('init', error);
  }
};

/**
 * Identifies the logged-in buyer so sessions can be filtered per customer.
 * Empty/falsy userId re-identifies as anonymous so logout does not leave a
 * sticky custom ID on the Clarity session. Clarity hashes the custom ID
 * client-side before it leaves the browser.
 */
export const identifyClarityUser = (userId?: string | null): void => {
  if (!canRun()) return;

  const clarity = getClarity();
  if (!clarity) return;

  const customId = userId?.trim() ? userId : ANONYMOUS_CLARITY_USER_ID;

  try {
    clarity.identify(customId);
  } catch (error) {
    reportClarityFailure('identify', error);
  }
};

/**
 * Tags the active session with buyer context (shipto/cg3/page) so recordings
 * and heatmaps can be filtered by the same dimensions Firebase reports.
 */
export const setClarityContext = (tags: ClarityContextTags): void => {
  if (!canRun()) return;

  const clarity = getClarity();
  if (!clarity) return;

  try {
    (Object.entries(tags) as [keyof ClarityContextTags, string][]).forEach(
      ([key, value]) => {
        clarity.setTag(key, value);
      }
    );
  } catch (error) {
    reportClarityFailure('setTag', error);
  }
};
